import { useCallback, useEffect, useRef } from 'react';

export const getTimelineEdgeDirection = (clientX, bounds, threshold = 50) => {
  if (!bounds) return null;
  if (clientX <= bounds.left + threshold) return 'left';
  if (clientX >= bounds.right - threshold) return 'right';
  return null;
};

const useTimelineEdgeAutoScroll = ({
  container,
  onScroll = () => {},
  intervalMs = 30,
  edgeThreshold = 50,
}) => {
  const intervalRef = useRef(null);
  const directionRef = useRef(null);
  const speedRef = useRef(1);
  const onScrollRef = useRef(onScroll);
  onScrollRef.current = onScroll;

  const stop = useCallback(() => {
    if (intervalRef.current !== null) clearInterval(intervalRef.current);
    intervalRef.current = null;
    directionRef.current = null;
    speedRef.current = 1;
  }, []);

  const start = useCallback(() => {
    if (!container || intervalRef.current !== null) return;
    intervalRef.current = setInterval(() => {
      const direction = directionRef.current;
      if (!direction) return;
      speedRef.current = Math.min(speedRef.current + 0.2, 10);
      const amount = 20 * speedRef.current;
      const maximum = Math.max(0, container.scrollWidth - container.clientWidth);
      const next = direction === 'left'
        ? Math.max(0, container.scrollLeft - amount)
        : Math.min(maximum, container.scrollLeft + amount);
      if (next === container.scrollLeft) return;
      container.scrollLeft = next;
      onScrollRef.current(next);
    }, intervalMs);
  }, [container, intervalMs]);

  const updatePointer = useCallback(clientX => {
    if (!container) return;
    const direction = getTimelineEdgeDirection(
      clientX,
      container.getBoundingClientRect(),
      edgeThreshold
    );
    if (!direction) {
      stop();
      return;
    }
    if (directionRef.current !== direction) speedRef.current = 1;
    directionRef.current = direction;
    start();
  }, [container, edgeThreshold, start, stop]);

  useEffect(() => stop, [stop]);

  return { updatePointer, stop };
};

export default useTimelineEdgeAutoScroll;
